import { useCallback, useEffect, useMemo, useRef } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useRoutedOverlayBase } from "./RoutedOverlayBase";
import { createOverlayEntryState, joinPath } from "./routedOverlayUtils";

export interface UseRoutedTabOptions<TTab extends string> {
  /** Slugs of the tabs that are currently available. */
  tabs: readonly TTab[];
  defaultTab: TTab;
  /** Pathname the tab segment follows. Defaults to the `RoutedOverlayBase` context. */
  basePath?: string;
  /**
   * Set to false while the available tabs are still loading so a deep link to a
   * tab that is about to appear is not rewritten to the default tab.
   */
  ready?: boolean;
  /**
   * Leave the default tab out of the path, so the bare base shows it. Use when the default
   * is only known after data loads: writing it into the URL is a second replace, which GA4
   * records as a second page view.
   */
  omitDefaultTab?: boolean;
  /** Replace the current history entry when switching tabs. Defaults to true. */
  replaceHistory?: boolean;
}

export interface RoutedTabControls<TTab extends string> {
  activeTab: TTab;
  setTab: (tab: TTab) => void;
  /**
   * True when the current path includes `activeTab` as its segment. False for a bare or
   * legacy tab-less base, and once the location leaves the base.
   */
  isTabInPath: boolean;
}

/**
 * Stores the active tab as the path segment after `basePath`. By default, tab switches
 * replace the current history entry so Back closes the overlay instead of stepping through tabs.
 */
export const useRoutedTab = <TTab extends string>({
  tabs,
  defaultTab,
  basePath,
  ready = true,
  omitDefaultTab = false,
  replaceHistory = true,
}: UseRoutedTabOptions<TTab>): RoutedTabControls<TTab> => {
  const location = useLocation();
  const navigate = useNavigate();
  const contextBase = useRoutedOverlayBase();
  const base = basePath ?? contextBase;

  const { withinBase, segment } = useMemo(() => {
    if (base === undefined) {
      return { withinBase: false, segment: undefined };
    }
    const match = matchPath(
      { path: `${joinPath(base)}/*`, end: false },
      location.pathname,
    );
    return {
      withinBase: !!match,
      segment: match?.params["*"]?.split("/")[0] || undefined,
    };
  }, [base, location.pathname]);

  const isKnownTab = !!segment && (tabs as readonly string[]).includes(segment);
  const isImplicitDefault = omitDefaultTab && segment === undefined;
  // Keep showing the last tab after the location leaves the base, so content does not
  // flash to the default tab while the overlay animates closed.
  const lastTabRef = useRef<TTab>(defaultTab);
  if (withinBase) {
    lastTabRef.current = isKnownTab ? (segment as TTab) : defaultTab;
  }
  const activeTab = lastTabRef.current;

  const tabPath = useCallback(
    (baseValue: string, tab: TTab) =>
      omitDefaultTab && tab === defaultTab ? joinPath(baseValue) : joinPath(baseValue, tab),
    [omitDefaultTab, defaultTab],
  );

  const setTab = useCallback(
    (tab: TTab) => {
      if (
        base === undefined ||
        !withinBase ||
        tab === segment ||
        (isImplicitDefault && tab === defaultTab)
      ) {
        return;
      }
      navigate(
        { pathname: tabPath(base, tab), search: location.search },
        {
          replace: replaceHistory,
          state: replaceHistory ? location.state : createOverlayEntryState(location),
        },
      );
    },
    [
      base,
      withinBase,
      segment,
      isImplicitDefault,
      defaultTab,
      tabPath,
      replaceHistory,
      navigate,
      location,
    ],
  );

  useEffect(() => {
    // Skip once the location leaves the base (e.g. the overlay is closing).
    if (base === undefined || !withinBase || !ready || isKnownTab || isImplicitDefault) {
      return;
    }
    navigate(
      { pathname: tabPath(base, defaultTab), search: location.search },
      { replace: true, state: location.state },
    );
  }, [
    base,
    withinBase,
    ready,
    isKnownTab,
    isImplicitDefault,
    defaultTab,
    tabPath,
    navigate,
    location.search,
    location.state,
  ]);

  return { activeTab, setTab, isTabInPath: withinBase && segment === activeTab };
};
